import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { supabase } from "@/integrations/supabase/client";

export interface OrgLaunchProgress {
  id: string;
  org_id: string;
  user_id: string;
  current_step: number;
  completed_steps: number[];
  progress_percent: number;
  is_complete: boolean;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

export const LAUNCH_STEPS = [
  { step: 1, title: "Brand Your Organization", icon: "🔥" },
  { step: 2, title: "Set Your Basics", icon: "🔥" },
  { step: 3, title: "Launch Your Website", icon: "🔥" },
] as const;

const tbl = () => supabase.from("org_launch_progress");

export const useOrgLaunchProgress = () => {
  const { orgId } = useOrg();
  const { user } = useAuth();
  return useQuery({
    queryKey: ["org_launch_progress", orgId, user?.id],
    enabled: !!orgId && !!user?.id,
    queryFn: async () => {
      const { data, error } = await tbl().select("*").eq("org_id", orgId).eq("user_id", user!.id).maybeSingle();
      if (error) throw error;
      return data as OrgLaunchProgress | null;
    },
  });
};

export const useInitLaunchProgress = () => {
  const qc = useQueryClient();
  const { orgId } = useOrg();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const { data, error } = await tbl()
        .upsert(
          { org_id: orgId, user_id: user!.id, current_step: 1, completed_steps: [], progress_percent: 0 },
          { onConflict: "org_id,user_id" },
        )
        .select()
        .single();
      if (error) throw error;
      return data as OrgLaunchProgress;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org_launch_progress"] }),
  });
};

export const useUpdateLaunchProgress = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<OrgLaunchProgress> & { id: string }) => {
      const { error } = await tbl()
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["org_launch_progress"] }),
  });
};
